import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { HomePage } from "./pages/HomePage";
import { ArOnboardingPage } from "./pages/ArOnboardingPage";
import { ApOnboardingPage } from "./pages/ApOnboardingPage";
import { InvoicePage } from "./pages/InvoicePage";
import { EditInvoicePage } from "./pages/EditInvoicePage";
import { CreateInvoicePage } from "./pages/CreateInvoicePage";
import { InvoicesListPage } from "./pages/InvoicesListPage";
import { CreditNotesListPage } from "./pages/CreditNotesListPage";
import { CreditNotePage } from "./pages/CreditNotePage";
import { SchedulesListPage } from "./pages/SchedulesListPage";
import { SchedulePage } from "./pages/SchedulePage";
import { CreateSchedulePage } from "./pages/CreateSchedulePage";
import { EditSchedulePage } from "./pages/EditSchedulePage";
import { CustomersListPage } from "./pages/CustomersListPage";
import { CustomerPage } from "./pages/CustomerPage";
import { ProductsListPage } from "./pages/ProductsListPage";
import { ProductPage } from "./pages/ProductPage";
import { TaxRatesPage } from "./pages/TaxRatesPage";
import { OrganisationSettingsPage } from "./pages/OrganisationSettingsPage";
import { AreaAccountingPage } from "./pages/AreaAccountingPage";
import { PaymentSettingsPage } from "./pages/PaymentSettingsPage";
import { BillsListPage } from "./pages/BillsListPage";
import { BillPage } from "./pages/BillPage";
import { BillPaymentReturnPage } from "./pages/BillPaymentReturnPage";
import { EditBillPage } from "./pages/EditBillPage";
import { ApprovalPoliciesPage } from "./pages/ApprovalPoliciesPage";
import { EmailForwardingSetupPage } from "./pages/EmailForwardingSetupPage";
import { GmailSettingsPage } from "./pages/GmailSettingsPage";
import { ReceivedEInvoicesPage } from "./pages/ReceivedEInvoicesPage";
import { SentEInvoicesPage } from "./pages/SentEInvoicesPage";
import { GmailOAuthCallbackPage } from "./pages/GmailOAuthCallbackPage";
import { GmailCandidatesPage } from "./pages/GmailCandidatesPage";
import { SuppliersListPage } from "./pages/SuppliersListPage";
import { SupplierCreditNotesListPage } from "./pages/SupplierCreditNotesListPage";
import { SupplierCreditNotePage } from "./pages/SupplierCreditNotePage";
import { PurchaseOrdersPage } from "./pages/PurchaseOrdersPage";
import { PurchaseOrderDetailPage } from "./pages/PurchaseOrderDetailPage";
import { DeliveryNotesPage } from "./pages/DeliveryNotesPage";
import { ExpensesListPage } from "./pages/ExpensesListPage";
import { ExpensesHomePage } from "./pages/ExpensesHomePage";
import { ExpensePage } from "./pages/ExpensePage";
import { MerchantsPage } from "./pages/MerchantsPage";
import { MerchantPage } from "./pages/MerchantPage";
import { CardTransactionsPage } from "./pages/CardTransactionsPage";
import { ReceiptsPage } from "./pages/ReceiptsPage";
import { ReceiptPage } from "./pages/ReceiptPage";
import { CardConnectionsPage } from "./pages/CardConnectionsPage";
import { BankConnectionsPage } from "./pages/BankConnectionsPage";
import { BankConnectionReturnPage } from "./pages/BankConnectionReturnPage";
import { BankAccountPage } from "./pages/BankAccountPage";
import { MccMappingsPage } from "./pages/MccMappingsPage";
import { ExpenseApprovalPoliciesPage } from "./pages/ExpenseApprovalPoliciesPage";
import { ExpensePolicyDocumentPage } from "./pages/ExpensePolicyDocumentPage";
import { DeliveryNoteDetailPage } from "./pages/DeliveryNoteDetailPage";
import { MatchPage } from "./pages/MatchPage";
import { SupplierPage } from "./pages/SupplierPage";
import { UsersPage } from "./pages/UsersPage";
import { AccountNotificationsPage } from "./pages/AccountNotificationsPage";
import { LoginPage } from "./pages/LoginPage";
import { RegisterPage } from "./pages/RegisterPage";
import { HandoffPage } from "./pages/HandoffPage";
import {
  BooksBalanceSheetPage,
  BooksCashFlowPage,
  BooksAccountsPage,
  BooksReportsPage,
  BooksBankLinesPage,
  BooksHomePage,
  BooksLedgerPage,
  BooksPostingsPage,
  BooksProfitAndLossPage,
  BooksSettingsPage,
  BooksRulesPage,
  BooksSetupPage,
  BooksTrialBalancePage,
} from "./pages/BooksPages";
import { SandboxBooksProvider } from "./components/SandboxBooksProvider";
import { DemoProvider } from "./demo/DemoProvider";
import { TryDemo } from "./demo/TryDemo";
import { ThemeProvider } from "./components/ThemeProvider";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AppShell } from "./components/AppShell";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import { ModulesProvider } from "./auth/ModulesContext";
import { DolfinAppProvider } from "./auth/DolfinAppProvider";
import { ChatProvider } from "./components/chat/ChatProvider";
import { BrandProvider } from "./branding/BrandProvider";
import { DemoSetupPage } from "./pages/DemoSetupPage";
import { DemoPage } from "./pages/DemoPage";
import { Toaster } from "@/components/ui/sonner";
import "./index.css";

const GOOGLE_CLIENT_ID = process.env.GOOGLE_CLIENT_ID!;

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/handoff" element={<HandoffPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/demo-setup" element={<DemoSetupPage />} />
      <Route path="/demo" element={<DemoPage />} />
      {/* A demo organisation's magic link lands here (eng-docs "AD - Demo Organisations"). */}
      <Route path="/try" element={<TryDemo />} />
      <Route path="/" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
      <Route path="/ar" element={<ProtectedRoute><ArOnboardingPage /></ProtectedRoute>} />
      <Route path="/ap" element={<ProtectedRoute><ApOnboardingPage /></ProtectedRoute>} />
      <Route path="/ar/accounting" element={<ProtectedRoute><AreaAccountingPage area="ar" /></ProtectedRoute>} />
      <Route path="/ap/accounting" element={<ProtectedRoute><AreaAccountingPage area="ap" /></ProtectedRoute>} />
      <Route path="/invoices" element={<ProtectedRoute><InvoicesListPage /></ProtectedRoute>} />
      <Route path="/invoices/new" element={<ProtectedRoute><CreateInvoicePage /></ProtectedRoute>} />
      <Route path="/invoices/:invoiceId" element={<ProtectedRoute><InvoicePage /></ProtectedRoute>} />
      <Route path="/invoices/:invoiceId/edit" element={<ProtectedRoute><EditInvoicePage /></ProtectedRoute>} />
      <Route path="/credit-notes" element={<ProtectedRoute><CreditNotesListPage /></ProtectedRoute>} />
      <Route path="/credit-notes/:creditNoteId" element={<ProtectedRoute><CreditNotePage /></ProtectedRoute>} />
      <Route path="/schedules" element={<ProtectedRoute><SchedulesListPage /></ProtectedRoute>} />
      <Route path="/schedules/new" element={<ProtectedRoute><CreateSchedulePage /></ProtectedRoute>} />
      <Route path="/schedules/:scheduleId" element={<ProtectedRoute><SchedulePage /></ProtectedRoute>} />
      <Route path="/schedules/:scheduleId/edit" element={<ProtectedRoute><EditSchedulePage /></ProtectedRoute>} />
      <Route path="/bills/email-forwarding" element={<ProtectedRoute><EmailForwardingSetupPage /></ProtectedRoute>} />
      {/* E-invoicing moved out of Payables: the registered party is the organisation itself. */}
      <Route path="/settings/e-invoicing" element={<Navigate to="/organisation/settings/e-invoicing" replace />} />
      <Route path="/received-e-invoices" element={<ProtectedRoute><ReceivedEInvoicesPage /></ProtectedRoute>} />
      <Route path="/sent-e-invoices" element={<ProtectedRoute><SentEInvoicesPage /></ProtectedRoute>} />
      <Route path="/settings/integrations/gmail" element={<ProtectedRoute><GmailSettingsPage /></ProtectedRoute>} />
      <Route path="/settings/integrations/gmail/:connectionId/candidates" element={<ProtectedRoute><GmailCandidatesPage /></ProtectedRoute>} />
      <Route path="/oauth/gmail/callback" element={<ProtectedRoute><GmailOAuthCallbackPage /></ProtectedRoute>} />
      {/* AP reviewer settings migrated to policy-based approvals. Old /bills/settings
          redirects to the new approval-policies page. */}
      <Route path="/bills/settings" element={<Navigate to="/bills/approval-policies" replace />} />
      <Route path="/bills/approval-policies" element={<ProtectedRoute><ApprovalPoliciesPage /></ProtectedRoute>} />
      <Route path="/bills" element={<ProtectedRoute><BillsListPage /></ProtectedRoute>} />
      <Route path="/bills/:billId" element={<ProtectedRoute><BillPage /></ProtectedRoute>} />
      <Route path="/bills/:billId/pay/return" element={<ProtectedRoute><BillPaymentReturnPage /></ProtectedRoute>} />
      <Route path="/bills/:billId/edit" element={<ProtectedRoute><EditBillPage /></ProtectedRoute>} />
      <Route path="/bills/:billId/match" element={<ProtectedRoute><MatchPage /></ProtectedRoute>} />
      <Route path="/purchase-orders" element={<ProtectedRoute><PurchaseOrdersPage /></ProtectedRoute>} />
      <Route path="/purchase-orders/:id" element={<ProtectedRoute><PurchaseOrderDetailPage /></ProtectedRoute>} />
      <Route path="/delivery-notes" element={<ProtectedRoute><DeliveryNotesPage /></ProtectedRoute>} />

      {/* Expenses. The mapping and accounting routes sit under /expenses/ because they are setup for
          this area, while the card, transaction and receipt routes are top-level objects in their own
          right. */}
      <Route path="/exp" element={<ProtectedRoute><ExpensesHomePage /></ProtectedRoute>} />
      <Route path="/expenses" element={<ProtectedRoute><ExpensesListPage /></ProtectedRoute>} />
      <Route path="/expenses/mcc-mappings" element={<ProtectedRoute><MccMappingsPage /></ProtectedRoute>} />
      <Route path="/expenses/accounting" element={<Navigate to="/organisation/settings/accounting" replace />} />
      <Route path="/expenses/approval-policies" element={<ProtectedRoute><ExpenseApprovalPoliciesPage /></ProtectedRoute>} />
      <Route path="/expenses/approval-policies/documents/:documentId" element={<ProtectedRoute><ExpensePolicyDocumentPage /></ProtectedRoute>} />
      <Route path="/expenses/merchants" element={<ProtectedRoute><MerchantsPage /></ProtectedRoute>} />
      <Route path="/expenses/merchants/:merchantId" element={<ProtectedRoute><MerchantPage /></ProtectedRoute>} />
      <Route path="/expenses/:expenseId" element={<ProtectedRoute><ExpensePage /></ProtectedRoute>} />
      <Route path="/card-transactions" element={<ProtectedRoute><CardTransactionsPage /></ProtectedRoute>} />
      <Route path="/receipts" element={<ProtectedRoute><ReceiptsPage /></ProtectedRoute>} />
      <Route path="/receipts/:receiptId" element={<ProtectedRoute><ReceiptPage /></ProtectedRoute>} />
      <Route path="/cards" element={<ProtectedRoute><CardConnectionsPage /></ProtectedRoute>} />
      <Route path="/delivery-notes/:id" element={<ProtectedRoute><DeliveryNoteDetailPage /></ProtectedRoute>} />
      <Route path="/suppliers" element={<ProtectedRoute><SuppliersListPage /></ProtectedRoute>} />
      <Route path="/supplier-credit-notes" element={<ProtectedRoute><SupplierCreditNotesListPage /></ProtectedRoute>} />
      <Route path="/supplier-credit-notes/:creditNoteId" element={<ProtectedRoute><SupplierCreditNotePage /></ProtectedRoute>} />
      <Route path="/suppliers/:supplierId" element={<ProtectedRoute><SupplierPage /></ProtectedRoute>} />
      <Route path="/customers" element={<ProtectedRoute><CustomersListPage /></ProtectedRoute>} />
      <Route path="/customers/:customerId" element={<ProtectedRoute><CustomerPage /></ProtectedRoute>} />
      <Route path="/products" element={<ProtectedRoute><ProductsListPage /></ProtectedRoute>} />
      <Route path="/products/:productId" element={<ProtectedRoute><ProductPage /></ProtectedRoute>} />
      <Route path="/tax-rates" element={<ProtectedRoute><TaxRatesPage /></ProtectedRoute>} />
      {/* One page per item under "Organisation" in the sidebar. */}
      <Route path="/organisation/settings" element={<ProtectedRoute><OrganisationSettingsPage section="details" /></ProtectedRoute>} />
      <Route path="/organisation/settings/ar" element={<ProtectedRoute><OrganisationSettingsPage section="ar" /></ProtectedRoute>} />
      <Route path="/organisation/settings/e-invoicing" element={<ProtectedRoute><OrganisationSettingsPage section="e-invoicing" /></ProtectedRoute>} />
      <Route path="/organisation/settings/accounting" element={<ProtectedRoute><OrganisationSettingsPage section="accounting" /></ProtectedRoute>} />
      <Route path="/organisation/bank-connections" element={<ProtectedRoute><BankConnectionsPage /></ProtectedRoute>} />
      <Route path="/organisation/bank-connections/return" element={<ProtectedRoute><BankConnectionReturnPage /></ProtectedRoute>} />
      <Route path="/organisation/bank-connections/accounts/:accountId" element={<ProtectedRoute><BankAccountPage /></ProtectedRoute>} />
      {/* Books (D35): the self-contained general ledger under src/books/. */}
      <Route path="/books" element={<ProtectedRoute><BooksHomePage /></ProtectedRoute>} />
      <Route path="/books/setup" element={<ProtectedRoute><BooksSetupPage /></ProtectedRoute>} />
      <Route path="/books/bank-lines" element={<ProtectedRoute><BooksBankLinesPage /></ProtectedRoute>} />
      <Route path="/books/reports" element={<ProtectedRoute><BooksReportsPage /></ProtectedRoute>} />
      <Route path="/books/accounts" element={<ProtectedRoute><BooksAccountsPage /></ProtectedRoute>} />
      <Route path="/books/cash-flow" element={<ProtectedRoute><BooksCashFlowPage /></ProtectedRoute>} />
      <Route path="/books/trial-balance" element={<ProtectedRoute><BooksTrialBalancePage /></ProtectedRoute>} />
      <Route path="/books/profit-and-loss" element={<ProtectedRoute><BooksProfitAndLossPage /></ProtectedRoute>} />
      <Route path="/books/balance-sheet" element={<ProtectedRoute><BooksBalanceSheetPage /></ProtectedRoute>} />
      <Route path="/books/ledger" element={<ProtectedRoute><BooksLedgerPage /></ProtectedRoute>} />
      <Route path="/books/postings" element={<ProtectedRoute><BooksPostingsPage /></ProtectedRoute>} />
      <Route path="/books/settings" element={<ProtectedRoute><BooksSettingsPage /></ProtectedRoute>} />
      <Route path="/books/rules" element={<ProtectedRoute><BooksRulesPage /></ProtectedRoute>} />
      <Route path="/organisation/users" element={<ProtectedRoute><UsersPage /></ProtectedRoute>} />
      <Route path="/account/notifications" element={<ProtectedRoute><AccountNotificationsPage /></ProtectedRoute>} />
      <Route path="/settings/payments" element={<ProtectedRoute><PaymentSettingsPage /></ProtectedRoute>} />
      <Route path="/settings/accounting" element={<Navigate to="/organisation/settings/accounting" replace />} />
    </Routes>
  );
}

export function App() {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <ThemeProvider>
        <BrandProvider>
          <TooltipProvider>
            <BrowserRouter>
              <AuthProvider>
                <ModulesProvider>
                  <DolfinAppProvider>
                    <DemoProvider>
                      <ChatProvider>
                        <SandboxBooksProvider>
                          <AppShell>
                            <AppRoutes />
                          </AppShell>
                        </SandboxBooksProvider>
                      </ChatProvider>
                    </DemoProvider>
                    <Toaster />
                  </DolfinAppProvider>
                </ModulesProvider>
              </AuthProvider>
            </BrowserRouter>
          </TooltipProvider>
        </BrandProvider>
      </ThemeProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
